import React, { useState } from "react";
import { Select } from "@pd/pd-ui-kit";
import { AxiosError } from "axios";
import { useTranslation } from "react-i18next";

import CommentModal from "~/pages/BaseStaff/ViewingBaseStaff/ui/CommentModal";
import { EmployeeService } from "~/entity/Employee/api/service";
import { checkRole } from "~/hooks/checkRole";
import {
  type StatusSelectOption,
  StatusSelectOptions,
} from "~/pages/BaseStaff/options";
import { usePermissions } from "~/shared/context/PermissionContext";
import { useNotify } from "~/shared/hooks/useNotify";
import { processArray } from "~/shared/lib/processArray";
import { InfoTooltip } from "~/shared/ui/Tooltip";

import { checkStatusAccess } from "./checkStatusAccess";

import s from "./Status.less";

interface IStatus {
  label?: string;
  refetch?: () => void;
  EmployeeId: string;
  positionId: string;
  allowedRolesForEditing: string[];
  statusValue?: StatusSelectOption;
  rejectReason?: string | null;
  instanceId?: string | number;
  cardView?: boolean;
  asField?: boolean;
}
const Status = (props: IStatus) => {
  const {
    refetch,
    EmployeeId,
    allowedRolesForEditing,
    statusValue,
    positionId,
    rejectReason,
    instanceId,
    cardView,
    asField,
  } = props;

  const { hasPermission } = usePermissions();
  const { t } = useTranslation();
  const role = checkRole();
  const hasRoleAccess = checkStatusAccess({
    allowedRoles: allowedRolesForEditing || [],
    role: String(role || ""),
    access: [],
  });
  const { notifySuccess, notifyError } = useNotify();
  const [isCommentModalOpen, setIsCommentModalOpen] = useState(false);

  const getStatusErrorMessage = (error: unknown) => {
    const fallback = t("errors.VALIDATION_ERROR");
    if (error instanceof AxiosError) {
      const message = (
        error.response?.data as
          | { message?: string | { message?: string } }
          | undefined
      )?.message;
      if (typeof message === "string" && message) {
        return message;
      }
      if (message && typeof message === "object" && message.message) {
        return message.message;
      }
    }
    return fallback;
  };

  const runStatusRequest = async (request: () => Promise<unknown>) => {
    try {
      await request();
      notifySuccess(t("common.сhange_was_successful"));
      refetch();
    } catch (error) {
      notifyError(getStatusErrorMessage(error));
    }
  };

  const onHandleSubmit = (data: {
    status: { label: string; value: string };
  }) => {
    switch (data.status.value) {
      case "verified":
        runStatusRequest(() =>
          EmployeeService.PUT.verifyStatus(EmployeeId, positionId),
        );
        break;

      case "rejected":
        setIsCommentModalOpen(true);
        break;

      case "sendCheck":
        runStatusRequest(() => EmployeeService.POST.sendCheck(EmployeeId));
        break;

      default:
        console.warn("Неизвестный статус:", data.status.value);
    }
  };

  const handleRejectSubmit = (comment: string) => {
    setIsCommentModalOpen(false);
    runStatusRequest(() =>
      EmployeeService.PUT.rejectStatus(EmployeeId, positionId, comment),
    );
  };

  const statusRejectTooltip =
    statusValue?.value === "rejected" && rejectReason?.trim()
      ? rejectReason.trim()
      : undefined;

  const statusOptionsWithDynamicPermissions = StatusSelectOptions.map((item) =>
    item.permissions
      ? {
          ...item,
          permissions: instanceId
            ? item.permissions.concat(
                item.permissions.map(
                  (permission) => `${permission}.${instanceId}`,
                ),
              )
            : item.permissions,
        }
      : item,
  );

  const statusOptionsByPermissions = hasRoleAccess
    ? statusOptionsWithDynamicPermissions
    : processArray(hasPermission, statusOptionsWithDynamicPermissions, {
        rule: "hasNotPermissionAction",
        modifyItem: (item) => ({ ...item, isDisabled: true }),
      });

  const hasAnyPermission =
    hasRoleAccess ||
    statusOptionsByPermissions.some(
      (item) => item["permissions"] && !item["isDisabled"],
    );

  const selectOptions = statusOptionsByPermissions.map((option) => ({
    value: option.value,
    label: option.label,
    disabled: Boolean(option.isDisabled),
    icon: option.icon,
  }));

  const onSelectStatus = (value?: string | number | null) => {
    if (value == null || String(value) === statusValue?.value) {
      return;
    }

    const next = statusOptionsByPermissions.find(
      (option) => option.value === value,
    );

    if (!next) {
      return;
    }

    onHandleSubmit({ status: next });
  };

  const select = (
    <Select
      value={statusValue?.value || undefined}
      options={selectOptions}
      onChange={onSelectStatus}
      disabled={asField ? !hasAnyPermission : undefined}
      variant={asField ? "default" : "borderless"}
      style={asField ? undefined : { width: "max-content" }}
      popupMatchSelectWidth={asField ? undefined : false}
      getPopupContainer={cardView ? undefined : () => document.body}
    />
  );

  return (
    <>
      <div
        className={cardView ? s.status_card : undefined}
        onClick={cardView ? undefined : (event) => event.stopPropagation()}
      >
        <InfoTooltip title={statusRejectTooltip} placement="top">
          {hasAnyPermission || asField ? (
            cardView ? (
              <div className={s.status_select_wrapper}>{select}</div>
            ) : (
              select
            )
          ) : (
            <div className={s.readonly}>
              {statusValue?.icon}
              <span>{statusValue?.label ? statusValue.label : ""}</span>
            </div>
          )}
        </InfoTooltip>
      </div>
      <CommentModal
        isOpen={isCommentModalOpen}
        onClose={() => {
          setIsCommentModalOpen(false);
        }}
        onSubmit={handleRejectSubmit}
      />
    </>
  );
};

export default Status;
